import { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Home, LayoutGrid, ClipboardList, Wallet, BookOpen, CalendarDays, Package, Clock, Percent, FileText, QrCode, Printer, ShieldAlert, LogOut,
  BarChart3, Settings, Umbrella, Menu, UtensilsCrossed, Smartphone, Building2, CreditCard, Layers, ShieldCheck, Scale, Palette, Activity,
} from 'lucide-react';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useAuth } from '@/contexts/AuthContext';
import { API_BASE } from '@/utils/api';
import { useIsModern } from './shared';

const A = ['business_admin'], S = ['business_admin', 'cashier', 'waiter'], C = ['business_admin', 'cashier'], ALL = ['business_admin', 'cashier', 'waiter', 'kitchen'];
export const BUSINESS_NAV = [
  ['Inicio', '/dashboard', Home, A],
  ['Mesas', '/pos', LayoutGrid, S, ['/layout-editor', '/visual-map']],
  ['Mis mesas', '/waiter', Smartphone, ['waiter']],
  ['Pedidos', '/kitchen', ClipboardList, ALL, ['/kitchen-tv']],
  ['Caja', '/cashier', Wallet, C, ['/cash-register']],
  ['Carta', '/menu-editor', BookOpen, A],
  ['Reservas', '/reservations', CalendarDays, S],
  ['Inventario', '/inventory', Package, A],
  ['Turnos', '/shifts', Clock, A],
  ['Promociones', '/promotions', Percent, A],
  ['Facturación', '/billing', FileText, C],
  ['QRs y carteles', '/qr-center', QrCode, A, ['/qr-manager']],
  ['Documentos', '/template-designer', Printer, A],
  ['Reportes', '/reports', BarChart3, A],
  ['Configuración', '/settings', Settings, A, ['/diagnostico-ip']],
  ['Alquileres', '/alquileres', Umbrella, []],
];
export const PLATFORM_NAV = [
  ['Resumen', 'overview', Activity], ['Negocios', 'businesses', Building2], ['Suscripciones', 'subscriptions', CreditCard],
  ['Planes', 'plans', Layers], ['Excepciones CUIT', 'cuit', ShieldCheck], ['Legales', 'legal', Scale], ['Tema', 'theme', Palette],
  ['Diagnóstico', 'diagnostics', Activity],
];
const BARE = ['/kitchen-tv', '/onboarding'];

let businessCache = {};
const useBusiness = (bid) => {
  const [biz, setBiz] = useState(businessCache[bid] || null);
  useEffect(() => {
    if (!bid) return setBiz(null);
    if (businessCache[bid]) return setBiz(businessCache[bid]);
    axios.get(`${API_BASE}/business`).then(r => { businessCache[bid] = r.data; setBiz(r.data); }).catch(() => {});
  }, [bid]);
  return biz;
};

const useClock = () => {
  const [now, setNow] = useState(new Date());
  useEffect(() => { const id = setInterval(() => setNow(new Date()), 30000); return () => clearInterval(id); }, []);
  return now;
};

const initials = (n = '') => n.replace(/\(.*\)/, '').trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

const NavList = ({ items, path, onNav, platform }) => {
  const { search } = useLocation();
  const tab = new URLSearchParams(search).get('tab') || 'overview';
  return (
    <nav className="flex flex-col gap-0.5 px-3" aria-label="Menú principal">
      {items.map(([label, to, Icon, , also = []]) => {
        const href = platform ? `/super-admin?tab=${to}` : to;
        const active = platform ? tab === to : path === to || also.some(a => path.startsWith(a));
        return (
          <Link key={label} to={href} onClick={onNav} aria-current={active ? 'page' : undefined}
            data-testid={`mnav-${platform ? to : to.slice(1)}`}
            className="flex items-center gap-3 h-10 px-3 rounded-lg text-[14px] font-medium transition-colors">
            <Icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.8} />{label}
          </Link>
        );
      })}
    </nav>
  );
};

const Brand = ({ biz, platform }) => (
  <div className="flex items-center gap-3 px-5 min-h-16 py-3">
    {!platform && biz?.logo_url
      ? <img src={biz.logo_url} alt="" className="w-9 h-9 rounded-lg object-cover bg-white" data-testid="mshell-logo" />
      : <span className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center text-sm font-bold">{platform || !biz ? <UtensilsCrossed className="w-5 h-5" /> : initials(biz.name)}</span>}
    <div className="min-w-0 leading-tight">
      <div className="font-bold text-[14px] tracking-wide uppercase leading-snug line-clamp-2 break-words" data-testid="mshell-brand">{platform ? 'QR Mesa' : biz?.name || '…'}</div>
      <div className="text-[12px] text-slate-400 truncate">{platform ? 'Plataforma' : biz ? (biz.business_type === 'beach_club' ? 'Parador de playa' : 'Restaurante') : ''}</div>
    </div>
  </div>
);

const ConfiguringBand = ({ biz }) => {
  const { checkAuth } = useAuth();
  const navigate = useNavigate();
  const exit = async () => {
    try { await axios.post(`${API_BASE}/admin/exit-business`); await checkAuth(); navigate('/super-admin'); }
    catch { toast.error('No se pudo volver al panel'); }
  };
  return (
    <div className="flex items-center gap-3 px-4 md:px-6 h-9 bg-[#b91c1c] text-white text-sm" role="status" data-testid="mshell-configuring-band">
      <ShieldAlert className="w-4 h-4 shrink-0" aria-hidden="true" />
      <span className="truncate"><b>Modo Super Admin</b> · Configurando <b data-testid="mshell-configuring-name">{biz?.name || '…'}</b></span>
      <button onClick={exit} className="ml-auto shrink-0 inline-flex items-center gap-1.5 h-7 px-3 rounded-md bg-white/15 hover:bg-white/25 font-semibold" data-testid="mshell-exit-business">
        <LogOut className="w-3.5 h-3.5" />Volver al panel admin
      </button>
    </div>
  );
};

export const ModernShell = ({ children }) => {
  const modern = useIsModern();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const bare = BARE.some(b => pathname.startsWith(b));
  const platform = pathname.startsWith('/super-admin');
  const configuring = user?.role === 'super_admin' && !!user?.active_business_id && !platform;
  const biz = useBusiness(modern && !bare && !platform && user ? (user.role === 'super_admin' ? user.active_business_id : user.business_id) : null);
  const now = useClock();
  if (!modern || bare || !user) return children;
  const role = user.role;
  const items = platform ? PLATFORM_NAV : BUSINESS_NAV.filter(([, , , roles]) => role === 'super_admin' ? true : roles.includes(role))
    .filter(([, to]) => !(role === 'super_admin' && to === '/waiter'));
  const nav = <NavList items={items} path={pathname} platform={platform} onNav={() => setOpen(false)} />;
  return (
    <div className="flex min-h-screen bg-background" data-testid="modern-shell">
      <aside className="mshell-side hidden md:flex w-60 shrink-0 flex-col sticky top-0 h-screen overflow-y-auto" data-surface="dark">
        <Brand biz={biz} platform={platform} />
        {nav}
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <div className="sticky top-0 z-40">
        {configuring && <ConfiguringBand biz={biz} />}
        <header className="mshell-top h-14 flex items-center gap-3 px-4 md:px-6" data-surface="dark">
          <button className="md:hidden p-2 -ml-2" onClick={() => setOpen(true)} aria-label="Abrir menú" data-testid="mshell-menu-btn"><Menu className="w-5 h-5" /></button>
          <span className="md:hidden font-bold uppercase text-sm truncate">{platform ? 'QR Mesa · Plataforma' : biz?.name}</span>
          <span className="hidden md:block font-semibold text-sm text-slate-300">{platform ? 'QR Mesa · Plataforma' : ''}</span>
          <div className="ml-auto flex items-center gap-4">
            <div className="text-right leading-tight hidden sm:block">
              <div className="text-[11px] text-slate-400">{now.toLocaleDateString('es-AR', { weekday: 'short', day: 'numeric', month: 'short' })}</div>
              <div className="text-sm font-bold" data-testid="mshell-clock">{now.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</div>
            </div>
            <Link to="/perfil" className="flex items-center gap-2" data-testid="mshell-user">
              <span className="w-8 h-8 rounded-full bg-[#dbeafe] text-[#1e3a8a] text-xs font-bold grid place-items-center">{platform ? 'SA' : initials(user.name)}</span>
              <span className="hidden lg:block text-sm">{platform || role === 'super_admin' ? 'Super Admin' : user.name}</span>
            </Link>
            <button onClick={async () => { await logout(); navigate('/'); }} className="p-2 rounded-lg hover:bg-white/10" aria-label="Cerrar sesión" title="Cerrar sesión" data-testid="mshell-logout">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </header>
        </div>
        <main className="mshell-main flex-1 min-w-0">{children}</main>
      </div>
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="left" className="mshell-side w-64 p-0 border-0" data-surface="dark">
          <SheetTitle className="sr-only">Menú</SheetTitle>
          <Brand biz={biz} platform={platform} />
          {nav}
        </SheetContent>
      </Sheet>
    </div>
  );
};
